import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { formatDateDDMMYYYY, formatINRForPdf } from "./format";

async function fetchArrayBuffer(publicPath) {
  const res = await fetch(publicPath, { cache: "no-cache" });
  if (!res.ok) throw new Error("Failed to load letterhead PDF");
  return await res.arrayBuffer();
}

/**
 * Sanitize a string for use in filenames across all platforms (Windows, Mac, Linux, iOS, Android)
 * Removes or replaces characters that are not allowed in filenames
 */
function sanitizeFilename(str) {
  if (!str) return "";
  // Replace invalid characters: < > : " | ? * \ / and control characters
  // eslint-disable-next-line no-control-regex
  let sanitized = String(str)
    .replace(/[<>:"|?*\\/\x00-\x1f]/g, "_") // Replace invalid chars with underscore
    .replace(/\s+/g, " ") // Normalize whitespace
    .trim(); // Remove leading/trailing spaces
  
  // Limit length to 200 characters to be safe for all platforms
  if (sanitized.length > 200) {
    sanitized = sanitized.substring(0, 200).trim();
  }
  
  // Remove trailing dots and spaces (not allowed on Windows)
  sanitized = sanitized.replace(/[\s.]+$/, "");
  
  // If empty after sanitization, return a default
  return sanitized || "Customer";
}

// Wrap a text into multiple lines within a max width using the provided font.
function wrapLines(text, maxWidth, font, fontSize) {
  const words = String(text || "").split(/\s+/);
  const lines = [];
  let current = "";

  for (const word of words) {
    if (!word) continue;
    const tentative = current ? `${current} ${word}` : word;
    const w = font.widthOfTextAtSize(tentative, fontSize);
    if (w > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = tentative;
    }
  }
  if (current) lines.push(current);
  if (!lines.length) lines.push("");
  return lines;
}

// Generic table renderer that measures text first, then draws borders and content.
function drawTable({
  page,
  font,
  fontBold,
  x,
  y,
  colWidths,
  rows,
  headerRows = 1,
  fontSize,
  lineHeight,
  color,
  boldRows = [], // Array of row indices that should be bold
}) {
  const cellPaddingX = 4;
  const cellPaddingY = 4;
  const totalWidth = colWidths.reduce((a, b) => a + b, 0);

  // Measure each row height
  const rowHeights = rows.map((row, rowIndex) => {
    let maxLines = 1;
    row.forEach((cell, colIndex) => {
      const maxTextWidth = colWidths[colIndex] - cellPaddingX * 2;
      const isHeader = rowIndex < headerRows;
      const isBold = isHeader || boldRows.includes(rowIndex);
      const f = isBold ? fontBold : font;
      const lines = wrapLines(cell, maxTextWidth, f, fontSize);
      if (lines.length > maxLines) maxLines = lines.length;
    });
    return cellPaddingY * 2 + maxLines * lineHeight;
  });

  const totalHeight = rowHeights.reduce((a, b) => a + b, 0);
  const bottomY = y - totalHeight;

  // Outer border
  page.drawRectangle({
    x,
    y: bottomY,
    width: totalWidth,
    height: totalHeight,
    borderColor: color,
    borderWidth: 0.7,
  });

  // Vertical lines
  let xCursor = x;
  colWidths.forEach((w, index) => {
    xCursor += w;
    if (index < colWidths.length - 1) {
      page.drawLine({
        start: { x: xCursor, y: bottomY },
        end: { x: xCursor, y: y },
        thickness: 0.5,
        color,
      });
    }
  });

  // Horizontal lines and text
  let yCursor = y;
  rows.forEach((row, rowIndex) => {
    const rowHeight = rowHeights[rowIndex];
    const rowBottom = yCursor - rowHeight;

    // Row separator (except top border)
    page.drawLine({
      start: { x, y: rowBottom },
      end: { x: x + totalWidth, y: rowBottom },
      thickness: 0.5,
      color,
    });

    // Draw text in each cell
    let cellX = x;
    row.forEach((cell, colIndex) => {
      const maxTextWidth = colWidths[colIndex] - cellPaddingX * 2;
      const isHeader = rowIndex < headerRows;
      const isBold = isHeader || boldRows.includes(rowIndex);
      const f = isBold ? fontBold : font;
      const lines = wrapLines(cell, maxTextWidth, f, fontSize);

      lines.forEach((line, i) => {
        const textY =
          yCursor - cellPaddingY - (i + 1) * lineHeight + lineHeight * 0.2;
        page.drawText(line, {
          x: cellX + cellPaddingX,
          y: textY,
          size: fontSize,
          font: f,
          color,
        });
      });

      cellX += colWidths[colIndex];
    });

    yCursor = rowBottom;
  });

  return yCursor; // new y position just under the table
}

export async function generateQuotePdfClient({
  letterheadPath = "/templates/letterhead.pdf",
  quote,
}) {
  const templateBytes = await fetchArrayBuffer(letterheadPath);
  const pdfDoc = await PDFDocument.load(templateBytes);
  
  // Prepare customer name and quote number for title and filename
  const customerName = sanitizeFilename(quote.customer?.name || "Customer");
  const quoteNo = quote.meta?.quoteNo || "Estimate";
  const sanitizedQuoteNo = quoteNo.replace(/\//g, "_");
  
  // Update PDF metadata with client name + quotation number
  const quoteTitle = `${customerName} ${sanitizedQuoteNo}`;
  pdfDoc.setTitle(quoteTitle);
  pdfDoc.setSubject(`Solar Rooftop Quotation - ${customerName}`);
  pdfDoc.setAuthor("Lunar Electric");
  pdfDoc.setCreator("Lunar Electric Quotation System");
  pdfDoc.setProducer("Lunar Electric Quotation System");
  
  const pages = pdfDoc.getPages();
  let page = pages[0];
  const { width, height } = page.getSize();

  const font = await pdfDoc.embedFont(StandardFonts.TimesRoman);
  const fontBold = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
  const black = rgb(0, 0, 0);
  const FONT_SIZE = 12;
  const LINE_HEIGHT = FONT_SIZE * 1.5;

  const marginX = 40;
  const left = marginX;
  const right = width - marginX;
  const footerMargin = 80; // keep clear of footer artwork
  let y = height - 120; // leave header area from your letterhead

  const drawText = (text, x, yPos, opts = {}) => {
    page.drawText(String(text), {
      x,
      y: yPos,
      size: opts.size || FONT_SIZE,
      font: opts.bold ? fontBold : font,
      color: black,
    });
  };

  const ensureSpace = (needed) => {
    if (y - needed < footerMargin) {
      page = pdfDoc.addPage([width, height]);
      y = height - 80;
    }
  };

  const drawParagraph = (text, opts = {}) => {
    const lines = wrapLines(text, right - left, font, opts.size || FONT_SIZE);
    lines.forEach((line) => {
      y -= LINE_HEIGHT;
      drawText(line, left, y, opts);
    });
  };

  // Header details
  drawText(
    `Date: ${formatDateDDMMYYYY(
      new Date(quote.meta.createdAt || Date.now())
    )}`,
    right - 220,
    y
  );
  drawText(
    `Estimate No.: ${quote.meta.quoteNo}`,
    right - 220,
    y - LINE_HEIGHT * 0.9,
    { bold: true }
  );

  // Customer block
  y -= LINE_HEIGHT * 2;
  drawText("To,", left, y, { bold: true });
  y -= LINE_HEIGHT;
  drawText(quote.customer.name || "-", left, y);
  if (quote.customer.address) {
    const addrLines = wrapLines(
      quote.customer.address,
      right - left,
      font,
      FONT_SIZE
    );
    addrLines.forEach((line) => {
      y -= LINE_HEIGHT;
      drawText(line, left, y);
    });
  } else {
    y -= LINE_HEIGHT;
  }
  y -= LINE_HEIGHT;
  drawText(`Phone: ${quote.customer.phone || "-"}`, left, y);
  y -= LINE_HEIGHT * 1.5;

  // Title line - center aligned
  const connectionLabel =
    quote.inputs.connection === "commercial" ? "commercial" : "residential";
  const titleText = `Cost Estimate for ${quote.derived.capacityKw}kW ${connectionLabel} solar rooftop`;
  const titleWidth = fontBold.widthOfTextAtSize(titleText, FONT_SIZE);
  const centerX = (left + right) / 2 - titleWidth / 2;
  drawText(titleText, centerX, y, { bold: true });
  y -= LINE_HEIGHT * 1.5;

  // Show price adjustment note if applicable
  if (quote.derived.priceAdjustment !== undefined && quote.derived.priceAdjustment !== 0) {
    const adjustmentText = `Note: Price adjusted by ${quote.derived.priceAdjustment >= 0 ? "+" : ""}${formatINRForPdf(quote.derived.priceAdjustment)}`;
    const adjustmentWidth = font.widthOfTextAtSize(adjustmentText, FONT_SIZE - 2);
    const adjustmentX = (left + right) / 2 - adjustmentWidth / 2;
    drawText(adjustmentText, adjustmentX, y, { size: FONT_SIZE - 2 });
    y -= LINE_HEIGHT;
  }

  // Pricing table
  const pricingRows = [];
  pricingRows.push([
    "Description",
    "UOM",
    "HSN",
    "GST %",
    "Amount",
  ]);
  // Show base price without GST (GST shown separately)
  pricingRows.push([
    `${quote.derived.capacityKw}kW Solar system*`,
    "1",
    "8541",
    "",
    formatINRForPdf(quote.derived.basePrice),
  ]);
  // Show logistics without GST (GST shown separately)
  const logisticsBase = quote.derived.logisticsBase || quote.derived.logisticsInclusive;
  pricingRows.push([
    "Installation, transportation, labor & DISCOM fees",
    "1",
    "9965, 9985",
    "",
    formatINRForPdf(logisticsBase),
  ]);
  // Show GST 5% on 70% of system price
  // Calculate if not present (for backward compatibility with old quotes)
  let gst5Percent = quote.derived.gst5Percent;
  let gst18Percent = quote.derived.gst18Percent;
  if (gst5Percent === undefined || gst18Percent === undefined) {
    // Fallback: calculate from total system price (basePrice + logisticsBase) for old quotes
    const systemTotal = quote.derived.basePrice + logisticsBase;
    const systemTotal70Percent = systemTotal * 0.7;
    const systemTotal30Percent = systemTotal * 0.3;
    gst5Percent = Math.round((5 / 100) * systemTotal70Percent);
    gst18Percent = Math.round((18 / 100) * systemTotal30Percent);
  }
  pricingRows.push([
    "GST 5% on 70% of system price",
    "1",
    "",
    "5%",
    formatINRForPdf(gst5Percent),
  ]);
  // Show GST 18% on 30% of system price
  pricingRows.push([
    "GST 18% on 30% of system price",
    "1",
    "",
    "18%",
    formatINRForPdf(gst18Percent),
  ]);
  // Calculate total as sum of all line items to ensure accuracy
  const totalInclusive = quote.derived.basePrice + logisticsBase + gst5Percent + gst18Percent;
  const totalRowIndex = pricingRows.length; // Index of the total row
  const totalLabel = quote.derived.subsidyApplicable && quote.derived.subsidyAmount > 0 
    ? "Total (without subsidy)" 
    : "Total";
  pricingRows.push([
    totalLabel,
    "",
    "",
    "",
    formatINRForPdf(totalInclusive),
  ]);
  let finalPriceRowIndex = null;
  if (quote.derived.subsidyApplicable && quote.derived.subsidyAmount > 0) {
    const finalWithSubsidy = Math.max(
      totalInclusive - quote.derived.subsidyAmount,
      0
    );
    pricingRows.push([
      "Less: Subsidy",
      "",
      "",
      "",
      `(${formatINRForPdf(quote.derived.subsidyAmount)})`,
    ]);
    finalPriceRowIndex = pricingRows.length; // Index of the final price row
    pricingRows.push([
      "Final price (with subsidy)",
      "",
      "",
      "",
      formatINRForPdf(finalWithSubsidy),
    ]);
  }
  // Column widths scaled to fit within page margins (removed one column)
  const totalTableWidth = right - left;
  const pricingColWidths = [
    0.40 * totalTableWidth, // Description
    0.10 * totalTableWidth, // UOM
    0.15 * totalTableWidth, // HSN
    0.10 * totalTableWidth, // GST
    0.25 * totalTableWidth, // Amount
  ];
  // Determine which rows should be bold (total row and final price row if applicable)
  const boldRows = [totalRowIndex];
  if (finalPriceRowIndex !== null) {
    boldRows.push(finalPriceRowIndex);
  }
  ensureSpace(pricingRows.length * LINE_HEIGHT * 2);
  y = drawTable({
    page,
    font,
    fontBold,
    x: left,
    y,
    colWidths: pricingColWidths,
    rows: pricingRows,
    headerRows: 1,
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    color: black,
    boldRows: boldRows,
  });
  y -= LINE_HEIGHT;

  // Subsidy structure note - only show when subsidy is applicable
  if (quote.derived.subsidyApplicable && quote.derived.subsidyAmount > 0) {
    ensureSpace(LINE_HEIGHT * 5);
    drawParagraph(
      "Note: We will assist with filling out the subsidy application. Given below is the subsidy structure:",
      { size: FONT_SIZE - 1 }
    );
    drawText(
      "Rs. 30,000 per kW up to 2 kW",
      left,
      y - LINE_HEIGHT,
      { size: FONT_SIZE - 1 }
    );
    drawText(
      "Rs. 18,000 per kW for additional capacity up to 3 kW",
      left,
      y - LINE_HEIGHT * 2,
      { size: FONT_SIZE - 1 }
    );
    drawText(
      "Rs. 78,000 Total subsidy for systems larger than 3 kW",
      left,
      y - LINE_HEIGHT * 3,
      { size: FONT_SIZE - 1 }
    );
    y -= LINE_HEIGHT * 4;
  }

  // Major Equipment makes
  const solarModulesMake =
    quote.derived.panelType === "DCR"
      ? "Orb Energy 540Wp DCR Bi-facial"
      : "Orb Energy 540Wp non-DCR Bi-facial";
  const eqRows = [
    ["Equipment", "Make"],
    ["Solar modules", solarModulesMake],
    ["Inverter", "Growatt / Solis / Sungrow"],
    ["DC Cables", "Siechem / Polycab / Unistar"],
    ["AC Cables", "RR Kable / Polycab / Havells"],
    ["ACDB Panel", "DB Electricals"],
  ];
  const eqColWidths = [160, right - left - 160];
  ensureSpace(eqRows.length * LINE_HEIGHT * 2);
  drawText("*Major Equipment makes", left, y, { bold: true });
  y -= LINE_HEIGHT;
  y = drawTable({
    page,
    font,
    fontBold,
    x: left,
    y,
    colWidths: eqColWidths,
    rows: eqRows,
    headerRows: 1,
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    color: black,
  });
  y -= LINE_HEIGHT;

  // Warranty
  const wRows = [
    ["Equipment", "Duration"],
    ["PV modules", "Product warranty: 10 years"],
    ["", "Power generation warranty: 25 years with linear degradation of 20%"],
    [
      "Inverter",
      "5-year warranty against manufacturing defects (from date of invoice)",
    ],
    ["Other equipment#", "1-year warranty against manufacturing defects"],
  ];
  const wColWidths = [160, right - left - 160];
  ensureSpace(wRows.length * LINE_HEIGHT * 2.4);
  drawText("Warranty", left, y, { bold: true });
  y -= LINE_HEIGHT;
  y = drawTable({
    page,
    font,
    fontBold,
    x: left,
    y,
    colWidths: wColWidths,
    rows: wRows,
    headerRows: 1,
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    color: black,
  });
  y -= LINE_HEIGHT * 0.5;
  drawParagraph(
    "#Consumables like Surge Protection Device (SPD), contactor & contactor coils, MCCB, MCB and fuses are not covered under warranty.",
    { size: FONT_SIZE - 2 }
  );
  y -= LINE_HEIGHT;

  // Payment terms
  const pRows = [
    ["Activity", "Amount"],
    ["Advance", "25%"],
    ["Prior to material dispatch", "70%"],
    ["Completion of synchronisation of the system", "5%"],
  ];
  const pColWidths = [right - left - 80, 80];
  ensureSpace(pRows.length * LINE_HEIGHT * 2);
  drawText("Payment terms", left, y, { bold: true });
  y -= LINE_HEIGHT;
  y = drawTable({
    page,
    font,
    fontBold,
    x: left,
    y,
    colWidths: pColWidths,
    rows: pRows,
    headerRows: 1,
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    color: black,
  });
  y -= LINE_HEIGHT;

  // Bank details
  ensureSpace(LINE_HEIGHT * 6);
  drawText("Bank details", left, y, { bold: true });
  y -= LINE_HEIGHT;
  drawText("Bank: SBI", left, y);
  y -= LINE_HEIGHT;
  drawText("Acc Name: POOJARAENERGY PRIVATE LIMITED", left, y);
  y -= LINE_HEIGHT;
  drawText("Acc No: 00000043692303280", left, y);
  y -= LINE_HEIGHT;
  drawText("IFSC: SBIN0064320", left, y);
  y -= LINE_HEIGHT;
  drawText("Note: Prices valid for 7 days.", left, y, {
    size: FONT_SIZE - 2,
  });

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  
  // Generate filename as: Client Name + Quotation Number
  // Use already sanitized values for cross-platform compatibility
  const filenameCustomerName = customerName; // Already sanitized above
  const filenameQuoteNo = sanitizedQuoteNo; // Already sanitized above (replaced / with _)
  const filename = `${filenameCustomerName} ${filenameQuoteNo}.pdf`;
  
  return { blob, url, filename };
}

